import { createContext, useCallback, useEffect } from "react";
import axios from "utils/axios";
import { getStore } from "store/reducers";
import {
  LOGIN,
  LOGOUT,
  SET_EMAIL,
  SET_INITIALIZED,
  SET_LOADING,
  SET_ERROR,
} from "store/reducers/actions";
import { useSelector } from "react-redux";
import { saveStateToStorage, getStoredState } from "store/reducers/auth";
import { authService } from "services/authService";
import LogoLoader from "components/LogoLoader";
import { processPendingReferral } from "hooks/useReferralTracking";
import { normalizeAuthUser } from "utils/adminAccess";
import { refreshSessionUserProfile } from "utils/authProfile";

const JWTContext = createContext(null);

const isTokenExpired = (token) => {
  // Si aucun token n'est fourni, le considérer comme expiré.
  if (!token) return true;

  try {
    // Décoder le payload du token.
    const base64Url = token.split(".")[1]; // Extraire la partie payload du JWT.
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/"); // Corriger l'encodage.
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    // Parser le payload et extraire le temps d'expiration (`exp`).
    const { exp } = JSON.parse(jsonPayload);
    return Date.now() >= exp * 1000;
  } catch (error) {
    // Si une erreur se produit, supposer que le token est invalide/expiré.
    return true;
  }
};

const setSession = (accessToken) => {
  if (accessToken) {
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
};

// Add a function to save state immediately
const saveAuthState = (state) => {
  if (state.isLoggedIn && state.accessToken) {
    saveStateToStorage(state);
  }
};

// Add a function to handle storage events from other tabs
const handleStorageChange = (event) => {
  if (event.key === "auth_state") {
    // If auth state was changed in another tab, reload the page to get the new state
    window.location.reload();
  }
};

export const JWTProvider = ({ children }) => {
  const store = getStore();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const isInitialized = useSelector((state) => state.auth.isInitialized);
  const user = useSelector((state) => state.auth.user);
  const isLoading = useSelector((state) => state.auth.isLoading);

  // Listen for changes to localStorage from other tabs
  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Save state on beforeunload (backup method)
  useEffect(() => {
    const handleBeforeUnload = () => {
      const state = store.getState().auth;
      saveAuthState(state);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [store]);

  // Save auth state whenever it changes
  useEffect(() => {
    if (isInitialized && !isLoading) {
      const state = store.getState().auth;
      saveAuthState(state);
    }
  }, [isLoggedIn, isInitialized, isLoading, user, store]);

  const restoreSessionUser = useCallback(
    async (savedUser, accessToken) => {
      setSession(accessToken);
      const user = await refreshSessionUserProfile(savedUser);
      store.dispatch({
        type: LOGIN,
        payload: {
          user,
          accessToken,
        },
      });
    },
    [store],
  );

  const logout = useCallback(async () => {
    store.dispatch({ type: SET_LOADING, payload: true });
    try {
      await axios.post("/api/auth/logout/");
      setSession(null);
      store.dispatch({ type: LOGOUT });
      // This will now work correctly
      saveStateToStorage(null);
    } catch (error) {
      setSession(null);
      store.dispatch({ type: LOGOUT });
      // This will now work correctly
      saveStateToStorage(null);
    } finally {
      store.dispatch({ type: SET_LOADING, payload: false });
    }
  }, [store]);

  useEffect(() => {
    const initAuth = async () => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const savedState = getStoredState();

        if (savedState?.accessToken) {
          if (isTokenExpired(savedState.accessToken)) {
            try {
              const response = await axios.post("/api/auth/refresh/");
              await restoreSessionUser(
                savedState.user,
                response.data.access_token,
              );
              setTimeout(() => {
                saveAuthState(store.getState().auth);
              }, 0);
            } catch (error) {
              logout();
            }
          } else {
            await restoreSessionUser(
              savedState.user,
              savedState.accessToken,
            );
          }
        } else {
          setSession(null);
          store.dispatch({ type: LOGOUT });
        }
      } catch (error) {
        logout();
      } finally {
        store.dispatch({ type: SET_INITIALIZED });
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    };

    initAuth();
  }, [store, logout, restoreSessionUser]);

  useEffect(() => {
    // Register logout callback
    authService.setLogoutCallback(logout);
  }, [logout]);

  const register = async (formData) => {
    store.dispatch({ type: SET_LOADING, payload: true });
    try {
      const response = await axios.post("/api/auth/register/", formData);
      return response.data;
    } catch (error) {

      // Extract error details from the response if available
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // If we have specific error details, return them
        if (errorData.error_details) {
          // Set error in state for global handling if needed
          store.dispatch({
            type: SET_ERROR,
            payload: errorData.message || "Registration failed",
          });

          // But also return the detailed error to the component for display
          const enhancedError = new Error(
            errorData.message || "Registration failed",
          );
          enhancedError.error_details = errorData.error_details;
          throw enhancedError;
        }
      }

      // If no specific error details found, throw the original error
      throw error;
    } finally {
      store.dispatch({ type: SET_LOADING, payload: false });
    }
  };

  const login = useCallback(
    async (email, password) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/login/", {
          email,
          password,
        });
        if (response.data.success) {
          const { access_token, user } = response.data;
          // First set the session
          setSession(access_token);

          // Then update Redux state
          store.dispatch({
            type: LOGIN,
            payload: {
              user,
              accessToken: access_token,
            },
          });

          // Immediately save the authentication state
          setTimeout(() => {
            saveAuthState(store.getState().auth);
          }, 0);

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        // Check if there's an OTP required error
        if (error.response?.data?.error?.otp_required) {
          store.dispatch({
            type: SET_EMAIL,
            payload: { email },
          });
          return error.response.data;
        }
        // Check if user was created with OAuth
        else if (error.response?.data?.oauth_provider) {
          // Return the error to be handled by the UI
          const oauthError = new Error(error.response.data.error);
          oauthError.isOAuthError = true;
          oauthError.oauthProvider = error.response.data.oauth_provider;
          throw oauthError;
        }
        // Handle server error details
        else if (error.response && error.response.data) {
          const errorData = error.response.data;

          // Extract detailed error information
          const errorMessage = errorData.message || "Login failed";
          const errorDetail = errorData.detail || null;
          const specificError = errorData.error || null;

          // Set general error in the Redux store
          store.dispatch({
            type: SET_ERROR,
            payload: errorMessage,
          });

          // Throw enhanced error with all available error information
          const enhancedError = new Error(errorMessage);
          enhancedError.statusCode = error.response.status;
          enhancedError.detail = errorDetail;
          enhancedError.specificError = specificError;
          throw enhancedError;
        } else {
          // For network errors or other issues without response data
          store.dispatch({
            type: SET_ERROR,
            payload:
              "Connection failed. Please check your internet connection.",
          });
          throw error;
        }
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const verifyOTP = useCallback(
    async (otp) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const state = store.getState().auth;
        const response = await axios.post("/api/auth/verify-otp/", {
          email: state.email,
          otp,
        });

        if (response.data.success) {
          const { access_token, user } = response.data;

          // First set the session
          setSession(access_token);

          // Then update Redux state
          store.dispatch({
            type: LOGIN,
            payload: {
              user,
              accessToken: access_token,
            },
          });

          // Immediately save the authentication state
          setTimeout(() => {
            saveAuthState(store.getState().auth);
          }, 0);

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "OTP verification failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const resendOTP = useCallback(
    async (email) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/resend-otp/", {
          email,
        });

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "OTP resend failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const resetPassword = useCallback(
    async (email) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/forgot-password/", {
          email,
        });
        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Password reset failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const changePasswordhandle = useCallback(
    async ({ email, new_password, confirm_password, token }) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/reset-password/", {
          email,
          new_password,
          confirm_password,
          token,
        });

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Password change failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const updateProfile = useCallback(
    async (data) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.put("/api/account/profile", data);
        const state = store.getState().auth;
        store.dispatch({
          type: LOGIN,
          payload: {
            user: response.data.user,
            accessToken: state.accessToken,
          },
        });

        // Immediately save the updated profile state
        setTimeout(() => {
          saveAuthState(store.getState().auth);
        }, 0);

        return response.data;
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.message || "Profile update failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const googleOAuthLogin = useCallback(
    async (token) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/google-oauth/", {
          token,
        });

        if (response.data.success) {
          const { access_token, user } = response.data;

          // First set the session
          setSession(access_token);

          // Then update Redux state
          store.dispatch({
            type: LOGIN,
            payload: {
              user,
              accessToken: access_token,
            },
          });

          // Immediately save the authentication state
          setTimeout(() => {
            saveAuthState(store.getState().auth);
          }, 0);

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.error || "Google OAuth login failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  const linkedinOAuthLogin = useCallback(
    async (code, state) => {
      store.dispatch({ type: SET_LOADING, payload: true });
      try {
        const response = await axios.post("/api/auth/linkedin-oauth/", {
          code,
          state,
        });

        if (response.data.success) {
          const { access_token, user } = response.data;

          // First set the session
          setSession(access_token);

          // Then update Redux state
          store.dispatch({
            type: LOGIN,
            payload: {
              user,
              accessToken: access_token,
            },
          });

          // Immediately save the authentication state
          setTimeout(() => {
            saveAuthState(store.getState().auth);
          }, 0);

          // Process any pending referral from localStorage
          processPendingReferral();

          return response.data;
        }
      } catch (error) {
        store.dispatch({
          type: SET_ERROR,
          payload: error.response?.data?.error || "LinkedIn OAuth login failed",
        });
        throw error;
      } finally {
        store.dispatch({ type: SET_LOADING, payload: false });
      }
    },
    [store],
  );

  return (
    <JWTContext.Provider
      value={{
        login,
        logout,
        register,
        verifyOTP,
        resetPassword,
        updateProfile,
        changePasswordhandle,
        resendOTP,
        googleOAuthLogin,
        linkedinOAuthLogin,
        isLoggedIn,
        isInitialized,
        user,
        isLoading,
      }}
    >
      {isLoading && <LogoLoader />}
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
